import { useRef, useState } from 'react';

import { AutoComplete, Checkbox, Input, Radio, Select, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { DatePicker } from 'components/DatePicker';
import useDebounce from 'hooks/useDebounce';
import { aykId, higherCommittee } from 'constant/static';
import { useGetFilteredUserListQuery, useLazyGetAuthorsQuery, useLazySearchE1ArticlesQuery } from 'services';

import { FILTER_DISPLAY_DATE_FORMAT, FILTER_VALUE_DATE_FORMAT, getOptionLabel, toIds } from '../offerFilterSchema';
import { useAuthorName, useE1ArticleTitles, useFilterSourceOptions } from '../offerFilterSources';

const { RangePicker } = DatePicker;

/** Radyo gruplarında "Tümü" seçeneğinin değeri; alanın boş olması demektir. */
const ALL = '__all__';

/** Sunucuda arama yapan girdilerde yazmayı bırakıp isteği göndermeden önceki bekleme. */
const REMOTE_SEARCH_DEBOUNCE_MS = 500;

/** Bugünkü filtre formuyla aynı: sorumlu heyette üst kurullar, görüş istenen heyette AYK listelenmez. */
const EXCLUDED_COMMITTEES = {
  ownerCommittee: higherCommittee,
  committeeContributions: [aykId],
};

const REWRITE = '0';

/** Kapsamlı gözden geçirme, ikmal ve tashih ile birlikte seçilemez (teklif formundaki kural). */
const COMPREHENSIVE_REVIEW = '2';

/** Seçili değerlerin seçenekleri listede olmasa da (yüklenemedi, filtrelendi, eski link) adıyla ya da id'siyle görünür. */
const withSelected = (options, ids, labels = {}) => [
  ...ids.filter((id) => !options.some((option) => option.value === id)).map((id) => ({ value: id, label: labels[id] ?? id })),
  ...options,
];

const useOptionLabel = () => {
  const { formatMessage } = useIntl();
  return (option) => getOptionLabel(option, formatMessage);
};

const LoadError = () => <div className="offer-filter-field__error">Liste yüklenemedi</div>;

const SourceSelect = ({ field, value, onChange, id }) => {
  const { options, isLoading, isError } = useFilterSourceOptions(field.source);
  const excluded = EXCLUDED_COMMITTEES[field.key] ?? [];
  const visibleOptions = options.filter((option) => !excluded.includes(Number(option.value)));
  const isMultiple = field.kind === 'multi';

  return (
    <>
      <Select
        id={id}
        mode={isMultiple ? 'multiple' : undefined}
        value={value}
        onChange={(next) => onChange(next)}
        options={withSelected(visibleOptions, toIds(value))}
        loading={isLoading}
        status={isError ? 'error' : undefined}
        placeholder={isMultiple ? 'Seçiniz' : 'Tümü'}
        optionFilterProp="label"
        getPopupContainer={(trigger) => trigger.parentElement}
        allowClear
        showSearch
      />
      {isError && <LoadError />}
    </>
  );
};

const SourceRadio = ({ field, value, onChange, labelId }) => {
  const { options, isLoading, isError } = useFilterSourceOptions(field.source);

  if (isLoading) {
    return <Spin size="small" />;
  }

  return (
    <>
      <Radio.Group
        aria-labelledby={labelId}
        buttonStyle="solid"
        value={value ?? ALL}
        onChange={(event) => onChange(event.target.value === ALL ? undefined : event.target.value)}
      >
        <Radio.Button value={ALL}>Tümü</Radio.Button>
        {withSelected(options, toIds(value)).map((option) => (
          <Radio.Button
            key={option.value}
            value={option.value}
          >
            {option.label}
          </Radio.Button>
        ))}
      </Radio.Group>
      {isError && <LoadError />}
    </>
  );
};

/**
 * Sunucuda arama yapan seçim. Arama `REMOTE_SEARCH_DEBOUNCE_MS` gecikmeyle gider; yalnız son aramanın sonucu gösterilir. Seçilen
 * değerin adı arama sonucundan, sayfa açılırken gelen değerinki `resolvedLabels`'tan alınır; ikisi de yoksa id
 * görünür ve değer yine kaldırılabilir.
 */
const RemoteSearchSelect = ({ id, mode, value, onChange, search, resolvedLabels, minLength, placeholder }) => {
  const { handleDebounce, cancelDebounce } = useDebounce();
  const requestRef = useRef(0);
  const knownLabelsRef = useRef({});
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('idle');

  results.forEach((option) => {
    knownLabelsRef.current[option.value] = option.label;
  });

  const handleSearch = (text) => {
    const query = text.trim();
    const request = ++requestRef.current;
    cancelDebounce();
    if (query.length < minLength) {
      setResults([]);
      setStatus('idle');
      return;
    }
    setStatus('loading');
    handleDebounce({
      delay: REMOTE_SEARCH_DEBOUNCE_MS,
      callback: async () => {
        try {
          const options = await search(query);
          if (request === requestRef.current) {
            setResults(options);
            setStatus('idle');
          }
        } catch {
          if (request === requestRef.current) {
            setResults([]);
            setStatus('error');
          }
        }
      },
    });
  };

  const notFoundContent = {
    loading: <Spin size="small" />,
    error: 'Arama yapılamadı',
    idle: null,
  }[status];

  return (
    <Select
      id={id}
      mode={mode}
      value={value}
      onChange={(next) => onChange(next)}
      options={withSelected(results, toIds(value), { ...knownLabelsRef.current, ...resolvedLabels })}
      onSearch={handleSearch}
      notFoundContent={notFoundContent}
      placeholder={placeholder}
      filterOption={false}
      getPopupContainer={(trigger) => trigger.parentElement}
      allowClear
      showSearch
    />
  );
};

const AuthorSearch = ({ id, value, onChange }) => {
  const [getAuthors] = useLazyGetAuthorsQuery();
  const { name } = useAuthorName(value);

  const search = async (fullName) => {
    const data = await getAuthors({ fullName }).unwrap();
    return data['hydra:member'].map((author) => ({ value: String(author.id), label: author.user.fullName }));
  };

  return (
    <RemoteSearchSelect
      id={id}
      value={value}
      onChange={onChange}
      search={search}
      resolvedLabels={name ? { [value]: name } : {}}
      minLength={1}
      placeholder="Yazar ara"
    />
  );
};

/**
 * 1. edisyon maddesi araması. `isTam`, bugünkü filtre formunun gönderdiği değerdir: ilgili madde aramasında `false`,
 * geçtiği maddeler aramasında boş.
 */
const E1ArticleSearch = ({ id, field, value, onChange }) => {
  const [searchE1Articles] = useLazySearchE1ArticlesQuery();
  const isMultiple = field.kind === 'multi';
  const { titles } = useE1ArticleTitles(toIds(value));

  const search = async (title) => {
    const articles = await searchE1Articles({ title, isTam: isMultiple ? '' : false }).unwrap();
    return articles.map((article) => ({ value: String(article.id), label: `${article.title} (${article.slug})` }));
  };

  return (
    <RemoteSearchSelect
      id={id}
      mode={isMultiple ? 'multiple' : undefined}
      value={value}
      onChange={onChange}
      search={search}
      resolvedLabels={titles}
      minLength={2}
      placeholder="Madde adı ara"
    />
  );
};

const OwnerInput = ({ id, value, onChange }) => {
  const [query, setQuery] = useState(value ?? '');
  const { handleDebounce } = useDebounce();
  const { data: users } = useGetFilteredUserListQuery({ fullName: query.trim() }, { skip: !query.trim() });

  const handleChange = (text) => {
    onChange(text);
    handleDebounce({ delay: REMOTE_SEARCH_DEBOUNCE_MS, callback: () => setQuery(text ?? '') });
  };

  return (
    <AutoComplete
      id={id}
      value={value}
      onChange={handleChange}
      options={query.trim() ? users?.map((user) => ({ key: user.id, value: user.fullName })) : []}
      placeholder="Kişi ara"
      getPopupContainer={(trigger) => trigger.parentElement}
      allowClear
    />
  );
};

const toDay = (date) => (date ? dayjs(date, FILTER_VALUE_DATE_FORMAT) : null);

const DateRangeInput = ({ id, value, onChange }) => (
  <RangePicker
    id={id}
    className="gx-w-100"
    format={FILTER_DISPLAY_DATE_FORMAT}
    placeholder={['Başlangıç', 'Bitiş']}
    allowEmpty={[true, true]}
    value={value ? [toDay(value.after), toDay(value.before)] : null}
    onChange={(dates) =>
      onChange(dates ? { after: dates[0]?.format(FILTER_VALUE_DATE_FORMAT), before: dates[1]?.format(FILTER_VALUE_DATE_FORMAT) } : undefined)
    }
    getPopupContainer={(trigger) => trigger.parentElement}
  />
);

const ChoiceRadio = ({ options, value, onChange, labelId }) => {
  const getLabel = useOptionLabel();
  return (
    <Radio.Group
      aria-labelledby={labelId}
      buttonStyle="solid"
      value={value === undefined ? ALL : String(value)}
      onChange={(event) => {
        const next = options.find((option) => String(option.value) === event.target.value);
        onChange(next?.value);
      }}
    >
      <Radio.Button value={ALL}>Tümü</Radio.Button>
      {options.map((option) => (
        <Radio.Button
          key={String(option.value)}
          value={String(option.value)}
        >
          {getLabel(option)}
        </Radio.Button>
      ))}
    </Radio.Group>
  );
};

/**
 * Yapılacak çalışma. Ansiklopedi maddesinde önce "Yeniden yazım" ya da "Güncelleme" seçilir, güncellemenin türleri
 * işaretlenir; atıf maddesinde dönüştürme türlerinden biri seçilir. "Güncelleme"nin kendi parametresi yoktur: tür
 * işaretlenmeden uygulanırsa alan boş sayılır.
 */
const WorkTypeControl = ({ field, value = [], onChange, oldTitleType, labelId }) => {
  const getLabel = useOptionLabel();
  const options = field.options.filter((option) => option.oldTitleType === oldTitleType);
  const updateOptions = options.filter((option) => option.value !== REWRITE);
  const hasUpdateValue = value.some((item) => item !== REWRITE);
  const [isUpdatePicked, setIsUpdatePicked] = useState(hasUpdateValue);

  if (oldTitleType === '1') {
    return (
      <Radio.Group
        aria-labelledby={labelId}
        value={value[0] ?? ALL}
        onChange={(event) => onChange(event.target.value === ALL ? undefined : [event.target.value])}
      >
        <Space direction="vertical">
          <Radio value={ALL}>Tümü</Radio>
          {options.map((option) => (
            <Radio
              key={option.value}
              value={option.value}
            >
              {getLabel(option)}
            </Radio>
          ))}
        </Space>
      </Radio.Group>
    );
  }

  const mode = (value.includes(REWRITE) && 'rewrite') || ((hasUpdateValue || isUpdatePicked) && 'update') || ALL;
  const hasReview = value.includes(COMPREHENSIVE_REVIEW);

  const handleModeChange = (nextMode) => {
    setIsUpdatePicked(nextMode === 'update');
    onChange(nextMode === 'rewrite' ? [REWRITE] : undefined);
  };

  return (
    <div className="offer-filter-work-type">
      <Radio.Group
        aria-labelledby={labelId}
        buttonStyle="solid"
        value={mode}
        onChange={(event) => handleModeChange(event.target.value)}
      >
        <Radio.Button value={ALL}>Tümü</Radio.Button>
        <Radio.Button value="rewrite">{getLabel(options.find((option) => option.value === REWRITE))}</Radio.Button>
        <Radio.Button value="update">{getLabel({ labelId: 'article.form.Update' })}</Radio.Button>
      </Radio.Group>
      {mode === 'update' && (
        <Checkbox.Group
          value={value}
          onChange={(next) => onChange(next)}
        >
          {updateOptions.map((option) => (
            <Checkbox
              key={option.value}
              value={option.value}
              disabled={option.value === COMPREHENSIVE_REVIEW ? value.length > 0 && !hasReview : hasReview}
            >
              {getLabel(option)}
            </Checkbox>
          ))}
        </Checkbox.Group>
      )}
    </div>
  );
};

/** Şemadaki alanın türüne ve kaynağına göre girdisini çizer. `draft`, bağımlı girdilerin üst alan değerini okumak içindir. */
export const FilterFieldControl = ({ field, value, onChange, draft, id, labelId }) => {
  const getLabel = useOptionLabel();

  if (field.source === 'authors') {
    return (
      <AuthorSearch
        id={id}
        value={value}
        onChange={onChange}
      />
    );
  }
  if (field.source === 'e1Articles') {
    return (
      <E1ArticleSearch
        id={id}
        field={field}
        value={value}
        onChange={onChange}
      />
    );
  }
  if (field.source === 'articleGroups') {
    return (
      <SourceRadio
        field={field}
        value={value}
        onChange={onChange}
        labelId={labelId}
      />
    );
  }
  if (field.source) {
    return (
      <SourceSelect
        id={id}
        field={field}
        value={value}
        onChange={onChange}
      />
    );
  }
  if (field.key === 'oldArticleWorkType') {
    return (
      <WorkTypeControl
        field={field}
        value={value}
        onChange={onChange}
        oldTitleType={draft.oldTitleType}
        labelId={labelId}
      />
    );
  }
  if (field.kind === 'boolean' || field.key === 'oldTitleType') {
    return (
      <ChoiceRadio
        options={field.options}
        value={value}
        onChange={onChange}
        labelId={labelId}
      />
    );
  }
  if (field.kind === 'select') {
    return (
      <Select
        id={id}
        value={value}
        onChange={(next) => onChange(next)}
        options={field.options.map((option) => ({ value: option.value, label: getLabel(option) }))}
        placeholder="Tümü"
        getPopupContainer={(trigger) => trigger.parentElement}
        allowClear
      />
    );
  }
  if (field.kind === 'flag') {
    return (
      <Checkbox
        id={id}
        checked={value === true}
        onChange={(event) => onChange(event.target.checked || undefined)}
      >
        {field.label}
      </Checkbox>
    );
  }
  if (field.kind === 'dateRange') {
    return (
      <DateRangeInput
        id={id}
        value={value}
        onChange={onChange}
      />
    );
  }
  if (field.key === 'owner') {
    return (
      <OwnerInput
        id={id}
        value={value}
        onChange={onChange}
      />
    );
  }
  return (
    <Input
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder="Aranacak ifade"
      allowClear
    />
  );
};
